<template>
    <button
        v-if="variant === 'cycle'"
        type="button"
        data-test-id="color-mode-selector"
        :data-value="colorMode"
        :title="`Vzhľad: ${current.label} (prepnúť na ${next.label})`"
        :aria-label="`Vzhľad: ${current.label}. Prepnúť na ${next.label}`"
        class="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-txt-caption transition-colors hover:bg-surface-subtle hover:text-txt-headings focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
        @click="setColorMode(next.value)"
    >
        <Icon :name="current.icon" class="size-5" aria-hidden="true" />
    </button>
    <div v-else class="inline-flex items-center gap-2 text-sm text-txt-body">
        <span v-if="showLabel" :id="`${groupName}-label`" class="font-medium">Vzhľad</span>
        <div
            role="radiogroup"
            :aria-label="showLabel ? undefined : 'Farebný režim'"
            :aria-labelledby="showLabel ? `${groupName}-label` : undefined"
            data-test-id="color-mode-selector"
            :data-value="colorMode"
            class="inline-flex items-center gap-0.5 rounded-full border border-default bg-surface-subtle p-0.5"
        >
            <label
                v-for="option in options"
                :key="option.value"
                :title="option.label"
                :data-mode="option.value"
                class="inline-flex size-7 cursor-pointer items-center justify-center rounded-full transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary-500"
                :class="colorMode === option.value ? 'bg-surface-card text-txt-brand-action shadow-sm' : 'text-txt-caption hover:text-txt-headings'"
            >
                <input
                    type="radio"
                    class="sr-only"
                    :name="groupName"
                    :value="option.value"
                    :checked="colorMode === option.value"
                    @change="setColorMode(option.value)"
                />
                <Icon :name="option.icon" class="size-4" aria-hidden="true" />
                <span class="sr-only">{{ option.label }}</span>
            </label>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ColorMode } from '@/utils/colorMode'

// `cycle` renders one icon button that steps through Auto → Svetlý → Tmavý.
withDefaults(defineProps<{ showLabel?: boolean; variant?: 'segmented' | 'cycle' }>(), {
    variant: 'segmented'
})
const { colorMode, setColorMode } = useDarkMode()
const groupName = `color-mode-${useId()}`

const options: { value: ColorMode; label: string; icon: string }[] = [
    { value: 'system', label: 'Auto', icon: 'heroicons:computer-desktop-20-solid' },
    { value: 'light', label: 'Svetlý', icon: 'heroicons:sun-20-solid' },
    { value: 'dark', label: 'Tmavý', icon: 'heroicons:moon-20-solid' }
]

const currentIndex = computed(() => Math.max(0, options.findIndex(option => option.value === colorMode.value)))
const current = computed(() => options[currentIndex.value]!)
const next = computed(() => options[(currentIndex.value + 1) % options.length]!)
</script>
